import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { RichTextEditor } from '@/components/ui/rich-text-editor'
import { Loader2, Upload, X, Image as ImageIcon, FileText } from 'lucide-react'
import { Episode, CreateEpisodeDTO, UpdateEpisodeDTO } from '@/services/admin/episodes.service'
import { useAdminModules } from '@/services/admin/modules.service'
import { useAdminCourses } from '@/services/admin/courses.service'
import { useEffect, useState, useRef } from 'react'
import {
  useUploadThumbnail,
  useUploadWorkbook,
  useUploadWorkbookCover,
} from '@/services/upload.service'
import { useToast } from '@/hooks/use-toast'
import { getErrorMessage } from '@/utils/error'

const episodeFormSchema = z.object({
  courseId: z.coerce.number().int().positive().optional(),
  moduleId: z.coerce.number().int().positive('Selecione um módulo'),
  number: z.coerce.number().int().positive('Número deve ser positivo'),
  title: z.string().min(3, 'Título deve ter no mínimo 3 caracteres').max(200),
  description: z.string().optional(),
  videoUrl: z.string().url('URL inválida').optional().or(z.literal('')),
  thumbnailUrl: z.string().url('URL inválida').optional().or(z.literal('')),
  workbookUrl: z.string().url('URL inválida').optional().or(z.literal('')),
  workbookCoverUrl: z.string().url('URL inválida').optional().or(z.literal('')),
  duration: z.coerce.number().int().positive().optional().or(z.literal('')),
  order: z.coerce.number().int().min(0).default(0),
  status: z.enum(['draft', 'published', 'archived', 'coming_soon']).default('draft'),
})

type EpisodeFormValues = z.infer<typeof episodeFormSchema>

interface EpisodeFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  episode?: Episode | null
  preselectedModuleId?: number
  onSubmit: (data: CreateEpisodeDTO | UpdateEpisodeDTO) => Promise<void>
  isSubmitting?: boolean
}

export function EpisodeFormDialog({
  open,
  onOpenChange,
  episode,
  preselectedModuleId,
  onSubmit,
  isSubmitting = false,
}: EpisodeFormDialogProps) {
  const { toast } = useToast()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const workbookInputRef = useRef<HTMLInputElement>(null)
  const workbookCoverInputRef = useRef<HTMLInputElement>(null)
  const [thumbnailPreview, setThumbnailPreview] = useState<string>('')
  const [workbookFile, setWorkbookFile] = useState<{ name: string; url: string } | null>(null)
  const [workbookCoverPreview, setWorkbookCoverPreview] = useState<string>('')
  const [selectedCourseId, setSelectedCourseId] = useState<number | undefined>(
    episode?.module?.courseId || undefined
  )

  const { data: courses } = useAdminCourses()
  const { data: modules } = useAdminModules(selectedCourseId)
  const { mutate: uploadThumbnail, isPending: isUploadingThumbnail } = useUploadThumbnail()
  const { mutate: uploadWorkbook, isPending: isUploadingWorkbook } = useUploadWorkbook()
  const { mutate: uploadWorkbookCover, isPending: isUploadingWorkbookCover } =
    useUploadWorkbookCover()

  const form = useForm<EpisodeFormValues>({
    resolver: zodResolver(episodeFormSchema),
    defaultValues: {
      courseId: undefined,
      moduleId: 0,
      number: 1,
      title: '',
      description: '',
      videoUrl: '',
      thumbnailUrl: '',
      workbookUrl: '',
      duration: '',
      order: 0,
      status: 'draft',
    },
  })

  // Reset form when episode changes or dialog opens
  useEffect(() => {
    if (open) {
      if (episode) {
        setSelectedCourseId(episode.module?.courseId)
        setThumbnailPreview(episode.thumbnailUrl || '')
        setWorkbookCoverPreview('')
        setWorkbookFile(
          episode.workbookUrl
            ? {
                name: episode.workbookUrl.split('/').pop() || 'workbook',
                url: episode.workbookUrl,
              }
            : null
        )

        // Usa reset para todos os campos
        form.reset({
          courseId: episode.module?.courseId,
          moduleId: episode.moduleId,
          number: episode.number,
          title: episode.title,
          description: episode.description || '',
          videoUrl: episode.videoUrl || '',
          thumbnailUrl: episode.thumbnailUrl || '',
          workbookUrl: episode.workbookUrl || '',
          workbookCoverUrl: '',
          duration: episode.duration || '',
          order: episode.order,
          status: episode.status,
        })
      } else {
        setSelectedCourseId(undefined)
        setThumbnailPreview('')
        setWorkbookCoverPreview('')
        setWorkbookFile(null)
        form.reset({
          courseId: undefined,
          moduleId: preselectedModuleId || 0,
          number: 1,
          title: '',
          description: '',
          videoUrl: '',
          thumbnailUrl: '',
          workbookUrl: '',
          workbookCoverUrl: '',
          duration: '',
          order: 0,
          status: 'draft',
        })
      }
    }
  }, [episode, open, preselectedModuleId, form])

  const handleThumbnailClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      // Validação client-side
      if (!file.type.startsWith('image/')) {
        toast({
          title: 'Formato inválido',
          description: 'Por favor, selecione um arquivo de imagem.',
          variant: 'destructive',
        })
        return
      }

      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: 'Arquivo muito grande',
          description: 'A imagem deve ter no máximo 10MB.',
          variant: 'destructive',
        })
        return
      }

      // Preview local
      const localUrl = URL.createObjectURL(file)
      setThumbnailPreview(localUrl)

      // Upload real
      uploadThumbnail(file, {
        onSuccess: (url) => {
          form.setValue('thumbnailUrl', url)
          setThumbnailPreview(url)
          toast({
            title: 'Thumbnail enviada!',
            description: 'Imagem redimensionada para 1024x1024px',
          })
          URL.revokeObjectURL(localUrl)
        },
        onError: (error: unknown) => {
          setThumbnailPreview('')
          toast({
            title: 'Erro ao enviar thumbnail',
            description: getErrorMessage(error, 'Tente novamente mais tarde.'),
            variant: 'destructive',
          })
          URL.revokeObjectURL(localUrl)
        },
      })
    }
  }

  const handleRemoveThumbnail = () => {
    form.setValue('thumbnailUrl', '')
    setThumbnailPreview('')
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleWorkbookClick = () => {
    workbookInputRef.current?.click()
  }

  const handleWorkbookChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Validação de tamanho ANTES de tudo
    if (file.size > 50 * 1024 * 1024) {
      toast({
        title: 'Arquivo muito grande',
        description: 'O workbook deve ter no máximo 50MB.',
        variant: 'destructive',
      })
      // Limpa o input
      if (workbookInputRef.current) {
        workbookInputRef.current.value = ''
      }
      return
    }

    // Validação de extensão (mais confiável que MIME type)
    const fileName = file.name.toLowerCase()
    const allowedExtensions = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.txt']
    const hasValidExtension = allowedExtensions.some((ext) => fileName.endsWith(ext))

    if (!hasValidExtension) {
      toast({
        title: 'Formato inválido',
        description: 'Use PDF, DOC, DOCX, XLS, XLSX, PPT ou TXT.',
        variant: 'destructive',
      })
      // Limpa o input
      if (workbookInputRef.current) {
        workbookInputRef.current.value = ''
      }
      return
    }

    // Upload real (após validações passarem)
    uploadWorkbook(file, {
      onSuccess: (url) => {
        form.setValue('workbookUrl', url)
        setWorkbookFile({ name: file.name, url })
        toast({
          title: 'Workbook enviado!',
          description: `Arquivo ${file.name} enviado com sucesso.`,
        })
      },
      onError: (error: unknown) => {
        toast({
          title: 'Erro ao enviar workbook',
          description: getErrorMessage(error, 'Tente novamente mais tarde.'),
          variant: 'destructive',
        })
        // Limpa o input em caso de erro
        if (workbookInputRef.current) {
          workbookInputRef.current.value = ''
        }
      },
    })
  }

  const handleRemoveWorkbook = () => {
    form.setValue('workbookUrl', '')
    setWorkbookFile(null)
    if (workbookInputRef.current) {
      workbookInputRef.current.value = ''
    }
  }

  const handleWorkbookCoverClick = () => {
    workbookCoverInputRef.current?.click()
  }

  const handleWorkbookCoverChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      // Validação client-side
      if (!file.type.startsWith('image/')) {
        toast({
          title: 'Formato inválido',
          description: 'Por favor, selecione um arquivo de imagem.',
          variant: 'destructive',
        })
        return
      }

      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: 'Arquivo muito grande',
          description: 'A imagem deve ter no máximo 10MB.',
          variant: 'destructive',
        })
        return
      }

      // Preview local
      const localUrl = URL.createObjectURL(file)
      setWorkbookCoverPreview(localUrl)

      // Upload real
      uploadWorkbookCover(file, {
        onSuccess: (url) => {
          form.setValue('workbookCoverUrl', url)
          setWorkbookCoverPreview(url)
          toast({
            title: 'Capa enviada!',
            description: 'Imagem redimensionada para 1280x720px (16:9)',
          })
          URL.revokeObjectURL(localUrl)
        },
        onError: (error: unknown) => {
          setWorkbookCoverPreview('')
          toast({
            title: 'Erro ao enviar capa',
            description: getErrorMessage(error, 'Tente novamente mais tarde.'),
            variant: 'destructive',
          })
          URL.revokeObjectURL(localUrl)
        },
      })
    }
  }

  const handleRemoveWorkbookCover = () => {
    form.setValue('workbookCoverUrl', '')
    setWorkbookCoverPreview('')
    if (workbookCoverInputRef.current) {
      workbookCoverInputRef.current.value = ''
    }
  }

  const handleSubmit = async (values: EpisodeFormValues) => {
    const { courseId, ...data } = values
    const processedData = {
      ...data,
      description: data.description || undefined,
      videoUrl: data.videoUrl || undefined,
      workbookUrl: data.workbookUrl || undefined,
      workbookCoverUrl: data.workbookCoverUrl || undefined,
      duration: data.duration ? Number(data.duration) : undefined,
    }

    await onSubmit(processedData)
    form.reset()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto overflow-x-hidden">
        <DialogHeader>
          <DialogTitle>{episode ? 'Editar Episódio' : 'Novo Episódio'}</DialogTitle>
          <DialogDescription>
            {episode ? 'Atualize as informações do episódio' : 'Preencha os dados do novo episódio'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            {!episode && (
              <FormField
                control={form.control}
                name="courseId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Curso *</FormLabel>
                    <Select
                      onValueChange={(value) => {
                        const courseId = Number(value)
                        field.onChange(courseId)
                        setSelectedCourseId(courseId)
                        form.setValue('moduleId', 0)
                      }}
                      value={field.value?.toString()}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione um curso" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {courses?.map((course) => (
                          <SelectItem key={course.id} value={course.id.toString()}>
                            {course.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="moduleId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Módulo *</FormLabel>
                  <Select
                    onValueChange={(value) => field.onChange(Number(value))}
                    value={field.value?.toString()}
                    disabled={!!episode || (!selectedCourseId && !episode)}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione um módulo" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {modules?.map((module) => (
                        <SelectItem key={module.id} value={module.id.toString()}>
                          {module.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {episode && (
                    <FormDescription>O módulo não pode ser alterado após a criação</FormDescription>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Número *</FormLabel>
                    <FormControl>
                      <Input type="number" min="1" {...field} />
                    </FormControl>
                    <FormDescription>Número do episódio no módulo</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="order"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Ordem</FormLabel>
                    <FormControl>
                      <Input type="number" min="0" {...field} />
                    </FormControl>
                    <FormDescription>Ordem de exibição</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Título *</FormLabel>
                  <FormControl>
                    <Input placeholder="Nome do episódio" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descrição</FormLabel>
                  <FormControl>
                    <RichTextEditor
                      value={field.value || ''}
                      onChange={field.onChange}
                      placeholder="Descrição do episódio (suporta formatação)"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="videoUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>URL do Vídeo</FormLabel>
                  <FormControl>
                    <Input placeholder="https://youtube.com/..." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="thumbnailUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Thumbnail/Ícone do Episódio</FormLabel>
                  <FormControl>
                    <div className="space-y-4">
                      {/* Preview da thumbnail */}
                      {thumbnailPreview ? (
                        <div className="relative w-full max-w-sm">
                          <div className="relative aspect-square rounded-lg overflow-hidden border-2 border-border bg-muted">
                            <img
                              src={thumbnailPreview}
                              alt="Preview da thumbnail"
                              className="w-full h-full object-cover"
                            />
                            {isUploadingThumbnail && (
                              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                <Loader2 className="w-8 h-8 text-white animate-spin" />
                              </div>
                            )}
                          </div>
                          <Button
                            type="button"
                            variant="destructive"
                            size="icon"
                            className="absolute -top-2 -right-2"
                            onClick={handleRemoveThumbnail}
                            disabled={isUploadingThumbnail}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="relative w-full max-w-sm">
                          <div
                            onClick={handleThumbnailClick}
                            className="aspect-square rounded-lg border-2 border-dashed border-border hover:border-primary transition-colors cursor-pointer bg-muted/50 hover:bg-muted flex flex-col items-center justify-center gap-2 group"
                          >
                            <div className="p-4 rounded-full bg-primary/10 group-hover:bg-primary/20 transition-colors">
                              <ImageIcon className="w-8 h-8 text-primary" />
                            </div>
                            <div className="text-center">
                              <p className="text-sm font-medium text-foreground">
                                Clique para fazer upload
                              </p>
                              <p className="text-xs text-muted-foreground mt-1">
                                JPG, PNG ou WebP (máx. 10MB)
                              </p>
                              <p className="text-xs text-muted-foreground">
                                Será redimensionada para 1024x1024px
                              </p>
                            </div>
                            <Upload className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                          </div>

                          {/* Loading overlay durante upload */}
                          {isUploadingThumbnail && (
                            <div className="absolute inset-0 bg-background/80 backdrop-blur-sm rounded-lg flex flex-col items-center justify-center gap-3">
                              <Loader2 className="w-8 h-8 text-primary animate-spin" />
                              <div className="text-center">
                                <p className="text-sm font-medium text-foreground">
                                  Enviando thumbnail...
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  Processando e redimensionando
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Input file oculto */}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={handleFileChange}
                        className="hidden"
                        disabled={isUploadingThumbnail}
                      />

                      {/* Input de URL manual (alternativa) */}
                      <div className="space-y-2">
                        <p className="text-xs text-muted-foreground">
                          Ou insira uma URL manualmente:
                        </p>
                        <Input
                          placeholder="https://..."
                          {...field}
                          onChange={(e) => {
                            field.onChange(e)
                            setThumbnailPreview(e.target.value)
                          }}
                          disabled={isUploadingThumbnail}
                        />
                      </div>
                    </div>
                  </FormControl>
                  <FormDescription>
                    Faça upload ou insira a URL da imagem/ícone do episódio (1024x1024px).
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="workbookUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Workbook/Material Complementar</FormLabel>
                  <FormControl>
                    <div className="space-y-4">
                      {/* Preview do workbook */}
                      {workbookFile ? (
                        <div className="relative">
                          <div className="flex items-center gap-3 p-4 rounded-lg border-2 border-border bg-muted/50">
                            <div className="p-3 rounded-lg bg-primary/10">
                              <FileText className="w-6 h-6 text-primary" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-foreground truncate">
                                {workbookFile.name}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {isUploadingWorkbook ? 'Enviando...' : 'Arquivo enviado'}
                              </p>
                            </div>
                            {isUploadingWorkbook && (
                              <Loader2 className="w-5 h-5 text-primary animate-spin" />
                            )}
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={handleRemoveWorkbook}
                              disabled={isUploadingWorkbook}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="relative">
                          <div
                            onClick={handleWorkbookClick}
                            className="relative flex items-center gap-3 p-6 rounded-lg border-2 border-dashed border-border hover:border-primary transition-colors cursor-pointer bg-muted/50 hover:bg-muted group"
                          >
                            <div className="p-3 rounded-lg bg-primary/10 group-hover:bg-primary/20 transition-colors">
                              <FileText className="w-6 h-6 text-primary" />
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-medium text-foreground">
                                Clique para fazer upload do workbook
                              </p>
                              <p className="text-xs text-muted-foreground mt-1">
                                PDF, DOC, DOCX, XLS, XLSX, PPT, TXT (máx. 50MB)
                              </p>
                            </div>
                            <Upload className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                          </div>

                          {/* Loading overlay durante upload */}
                          {isUploadingWorkbook && (
                            <div className="absolute inset-0 bg-background/80 backdrop-blur-sm rounded-lg flex flex-col items-center justify-center gap-3">
                              <Loader2 className="w-8 h-8 text-primary animate-spin" />
                              <div className="text-center">
                                <p className="text-sm font-medium text-foreground">
                                  Enviando workbook...
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  Aguarde enquanto processamos o arquivo
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Input file oculto */}
                      <input
                        ref={workbookInputRef}
                        type="file"
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        onChange={handleWorkbookChange}
                        className="hidden"
                        disabled={isUploadingWorkbook}
                      />

                      {/* Input de URL manual (alternativa) */}
                      <div className="space-y-2">
                        <p className="text-xs text-muted-foreground">
                          Ou insira uma URL manualmente:
                        </p>
                        <Input
                          placeholder="https://..."
                          {...field}
                          onChange={(e) => {
                            field.onChange(e)
                            if (e.target.value) {
                              setWorkbookFile({
                                name: e.target.value.split('/').pop() || 'workbook',
                                url: e.target.value,
                              })
                            } else {
                              setWorkbookFile(null)
                            }
                          }}
                          disabled={isUploadingWorkbook}
                        />
                      </div>
                    </div>
                  </FormControl>
                  <FormDescription>
                    Faça upload ou insira a URL do material complementar (PDF, DOC, XLS, etc).
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="workbookCoverUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Capa do Workbook (16:9)</FormLabel>
                  <FormControl>
                    <div className="space-y-4">
                      {/* Preview da capa do workbook */}
                      {workbookCoverPreview ? (
                        <div className="relative w-full max-w-2xl">
                          <div className="relative aspect-video rounded-lg overflow-hidden border-2 border-border bg-muted">
                            <img
                              src={workbookCoverPreview}
                              alt="Preview da capa do workbook"
                              className="w-full h-full object-cover"
                            />
                            {isUploadingWorkbookCover && (
                              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                <Loader2 className="w-8 h-8 text-white animate-spin" />
                              </div>
                            )}
                          </div>
                          <Button
                            type="button"
                            variant="destructive"
                            size="icon"
                            className="absolute -top-2 -right-2"
                            onClick={handleRemoveWorkbookCover}
                            disabled={isUploadingWorkbookCover}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="relative w-full max-w-2xl">
                          <div
                            onClick={handleWorkbookCoverClick}
                            className="aspect-video rounded-lg border-2 border-dashed border-border hover:border-primary transition-colors cursor-pointer bg-muted/50 hover:bg-muted flex flex-col items-center justify-center gap-2 group"
                          >
                            <div className="p-4 rounded-full bg-primary/10 group-hover:bg-primary/20 transition-colors">
                              <ImageIcon className="w-8 h-8 text-primary" />
                            </div>
                            <div className="text-center">
                              <p className="text-sm font-medium text-foreground">
                                Clique para fazer upload da capa
                              </p>
                              <p className="text-xs text-muted-foreground mt-1">
                                JPG, PNG ou WebP (máx. 10MB)
                              </p>
                              <p className="text-xs text-muted-foreground">
                                Será redimensionada para 1280x720px (16:9)
                              </p>
                            </div>
                            <Upload className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                          </div>

                          {/* Loading overlay durante upload */}
                          {isUploadingWorkbookCover && (
                            <div className="absolute inset-0 bg-background/80 backdrop-blur-sm rounded-lg flex flex-col items-center justify-center gap-3">
                              <Loader2 className="w-8 h-8 text-primary animate-spin" />
                              <div className="text-center">
                                <p className="text-sm font-medium text-foreground">
                                  Enviando capa...
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  Processando e redimensionando
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Input file oculto */}
                      <input
                        ref={workbookCoverInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={handleWorkbookCoverChange}
                        className="hidden"
                        disabled={isUploadingWorkbookCover}
                      />

                      {/* Input de URL manual (alternativa) */}
                      <div className="space-y-2">
                        <p className="text-xs text-muted-foreground">
                          Ou insira uma URL manualmente:
                        </p>
                        <Input
                          placeholder="https://..."
                          {...field}
                          onChange={(e) => {
                            field.onChange(e)
                            setWorkbookCoverPreview(e.target.value)
                          }}
                          disabled={isUploadingWorkbookCover}
                        />
                      </div>
                    </div>
                  </FormControl>
                  <FormDescription>
                    Faça upload ou insira a URL da capa que aparecerá na área de preview do workbook
                    (proporção 16:9).
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="duration"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Duração (minutos)</FormLabel>
                    <FormControl>
                      <Input type="number" min="1" placeholder="30" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Status do episódio" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="draft">Rascunho</SelectItem>
                        <SelectItem value="published">Publicado</SelectItem>
                        <SelectItem value="archived">Arquivado</SelectItem>
                        <SelectItem value="coming_soon">Em Breve</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {episode ? 'Atualizar' : 'Criar'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
